import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getToken } from '../../api/client';
import { jobsApi } from '../../api/jobs';
import {
  workspacesApi,
  setWorkspaceId,
  type WorkspaceInfo,
} from '../../api/workspaces';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export default function WorkspacesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCompanyCode, setNewCompanyCode] = useState('');

  // The ADMIN management page lists EVERYTHING (AdminOnly endpoint) — the
  // daily switcher's /workspaces/mine is membership-only for every role now.
  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['workspaces-all'],
    queryFn: workspacesApi.listAll,
  });

  // Nightly-sync job health (in-memory snapshot — resets on API restart).
  const { data: jobStatuses } = useQuery({
    queryKey: ['admin-jobs'],
    queryFn: jobsApi.list,
    refetchInterval: 30_000,
    retry: false,
  });
  const syncJob = jobStatuses?.find(j => j.jobName === 'WorkspaceSyncJob');

  const [deletingWorkspaceName, setDeletingWorkspaceName] = useState('');

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      if (deletingWorkspaceName) {
        addToast(`Darbo sritis „${deletingWorkspaceName}“ ištrinta`, 'success');
        setDeletingWorkspaceName('');
      }
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.activate(id),
    onSuccess: (ws: WorkspaceInfo) => {
      setWorkspaceId(ws.id);
      queryClient.invalidateQueries();
    },
  });

  const toggleAiMutation = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      workspacesApi.update(id, { aiEgressDisabled: disabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const handleSyncNow = async (id: string) => {
    setSyncingId(id);
    try {
      const r = await workspacesApi.syncNow(id);
      addToast(`Sinchronizuota: ${r.recordsImported} įrašai (${Math.round(r.durationMs / 100) / 10} s)`, 'success');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Sinchronizacija nepavyko', 'error');
    } finally {
      setSyncingId(null);
    }
  };

  // Sequential (each sync is a full import; the endpoint is rate-limited).
  const handleSyncAll = async () => {
    const mysqlWorkspaces = (workspaces ?? []).filter(ws => ws.sourceKind === 'mysql');
    if (mysqlWorkspaces.length === 0) return;
    setSyncingAll(true);
    try {
      let ok = 0;
      for (const ws of mysqlWorkspaces) {
        try {
          await workspacesApi.syncNow(ws.id);
          ok++;
        } catch { /* per-row toast noise skipped for the bulk pass */ }
      }
      addToast(`Sinchronizuota darbo sričių: ${ok} iš ${mysqlWorkspaces.length}`, ok === mysqlWorkspaces.length ? 'success' : 'error');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newCompanyCode.trim()) return;
    setCreating(true);
    try {
      const ws = await workspacesApi.create({
        name: newName.trim(),
        companyName: newName.trim(),
        companyCode: newCompanyCode.trim(),
      });
      await activateMutation.mutateAsync(ws.id);
      setNewName('');
      setNewCompanyCode('');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate('/');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Nepavyko sukurti darbo srities');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ws: WorkspaceInfo) => {
    if (!confirm(`Ištrinti darbo sritį „${ws.name}“? Šio veiksmo atšaukti negalima.`)) return;
    setDeletingWorkspaceName(ws.name);
    deleteMutation.mutate(ws.id);
  };

  const handleExport = (ws: WorkspaceInfo) => {
    // Download via authenticated fetch (cookie + Bearer when in memory)
    const token = getToken();
    const url = `${import.meta.env.VITE_API_URL ?? '/api'}/workspaces/${ws.id}/export`;
    fetch(url, {
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Eksportas nepavyko');
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${ws.name}.db`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => alert(err.message));
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-gray-900">Darbo sritys</h1>

      {/* Create new company — a workspace exists ONLY as a client company
          (imported db or a fresh company). The I_Kodas code is what ties the
          workspace to the client's shop account via the membership join. */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-3">Nauja įmonė</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="Įmonės pavadinimas"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            type="text"
            className="w-44 px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="Įmonės kodas"
            value={newCompanyCode}
            onChange={(e) => setNewCompanyCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={handleCreate}
            disabled={creating || !newName.trim() || !newCompanyCode.trim()}
          >
            {creating ? 'Kuriama…' : 'Sukurti'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Sukuriama nauja įmonės darbo sritis. Kodas turi sutapti su kliento įmonės kodu —
          juo kliento paskyrai suteikiamas prisijungimas.
        </p>
      </div>

      {/* Workspace list */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <h2 className="font-semibold text-gray-900">Visos darbo sritys</h2>
          {/* Nightly-sync job health (green ✓ / red with error / amber >30h or not-yet-run) */}
          {syncJob && (
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full border ${
                !syncJob.succeeded
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : Date.now() - new Date(syncJob.lastRunUtc).getTime() > 30 * 3600_000
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-green-50 text-green-700 border-green-200'
              }`}
              title={syncJob.errorMessage ?? undefined}
            >
              {syncJob.succeeded ? '✓' : '!'} Naktinis sinchronizavimas: {new Date(syncJob.lastRunUtc).toLocaleString('lt-LT')}
            </span>
          )}
          <div className="flex-1" />
          {(workspaces ?? []).some(ws => ws.sourceKind === 'mysql') && (
            <button
              className="px-3 py-1.5 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:border-primary-400 disabled:opacity-50"
              onClick={handleSyncAll}
              disabled={syncingAll || syncingId !== null}
            >
              {syncingAll ? 'Sinchronizuojama…' : 'Sinchronizuoti visus'}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">Pavadinimas</th>
                <th className="text-left">Sukurta</th>
                <th className="text-left">Atidaryta</th>
                <th className="text-left">Būsena</th>
                <th className="text-left">Šaltinis</th>
                <th className="text-right">Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {workspaces?.map((ws: WorkspaceInfo) => (
                <tr key={ws.id} className={`border-b border-gray-50 ${ws.isActive ? 'bg-primary-50/50' : ''}`}>
                  <td className="font-medium">{ws.name}</td>
                  <td className="text-gray-500">{formatDate(ws.createdAt)}</td>
                  <td className="text-gray-500">{formatDate(ws.lastOpenedAt)}</td>
                  <td>
                    {ws.isActive ? (
                      <span className="text-xs font-medium text-green-600">Aktyvi</span>
                    ) : (
                      <span className="text-xs text-gray-400">Neaktyvi</span>
                    )}
                  </td>
                  <td className="text-xs">
                    {ws.sourceKind === 'mysql' ? (
                      <div>
                        <span className="font-medium text-primary-700">☁ sinchronizuojama</span>
                        <div className="text-gray-400">
                          {ws.lastSyncedAtUtc
                            ? `Atnaujinta: ${new Date(ws.lastSyncedAtUtc).toLocaleString('lt-LT')}`
                            : 'Dar nesinchronizuota'}
                        </div>
                        <button
                          className="text-primary-600 hover:underline mt-0.5"
                          onClick={() => handleSyncNow(ws.id)}
                          disabled={syncingId === ws.id}
                        >
                          {syncingId === ws.id ? 'Sinchronizuojama…' : 'Sinchronizuoti dabar'}
                        </button>
                      </div>
                    ) : (
                      // Cloud databases are linked automatically at sign-in
                      // (CloudWorkspaceProvisioner) — there is no manual linking.
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="text-right space-x-2">
                    {!ws.isActive && (
                      <button
                        className="text-xs text-primary-600 hover:underline"
                        onClick={() => activateMutation.mutate(ws.id)}
                        disabled={activateMutation.isPending}
                      >
                        Aktyvizuoti
                      </button>
                    )}
                    <button
                      className={`text-xs ${ws.aiEgressDisabled ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                      onClick={() => toggleAiMutation.mutate({ id: ws.id, disabled: !ws.aiEgressDisabled })}
                      disabled={toggleAiMutation.isPending}
                      title={ws.aiEgressDisabled ? 'AI išjungtas — spustelėkite, kad įjungtumėte' : 'AI įjungtas — spustelėkite, kad išjungtumėte'}
                    >
                      {ws.aiEgressDisabled ? '🔒 AI Off' : '🤖 AI On'}
                    </button>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => handleExport(ws)}
                    >
                      Eksportuoti
                    </button>
                    {!ws.isActive && (
                      <button
                        className="text-xs text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(ws)}
                        disabled={deleteMutation.isPending}
                      >
                        Ištrinti
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!workspaces || workspaces.length === 0) && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-4">
                    Darbo sričių dar nėra — kliento įmonės darbo sritis atsiranda automatiškai, kai jos naudotojas prisijungia per integravizija.lt. Galite sukurti naują ir rankiniu būdu aukščiau.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

